/**
 * Undo Cloudflare's Email Obfuscation in HTML we are about to render ourselves.
 *
 * Cloudflare (Scrape Shield → Email Obfuscation) rewrites a mailto address into
 *
 *   <a href="/cdn-cgi/l/email-protection#<hex>"
 *      class="__cf_email__" data-cfemail="<hex>">[email&#160;protected]</a>
 *
 * and ships a script at /cdn-cgi/scripts/…/email-decode.min.js that swaps the
 * real address back in. Our previews render into `<iframe srcdoc>` and into a
 * `blob:` URL — neither can resolve that root-relative script, so the decoder
 * never runs and the operator reads a literal "[email protected]" where the
 * scenario's sender address should be (reported 2026-09-22, awareness-page
 * preview inside the scenario drawer).
 *
 * Decoding here works wherever the markup came from — stored HTML, or a
 * text/html response rewritten in transit — because by the time a preview holds
 * the string, the marker is in it either way.
 *
 * THIS IS A PREVIEW-SIDE REPAIR, NOT THE ROOT CAUSE. If Cloudflare is
 * obfuscating our own responses, every other consumer of that HTML still sees
 * the placeholder; the real fix is to scope Email Obfuscation off the routes
 * that serve this HTML. Diagnosing that needs a deployed environment, which is
 * why it is not done here.
 *
 * The encoding: the first hex byte is the XOR key, every following byte is a
 * character of the address XORed with it.
 */

/** Decode one `data-cfemail` value. Returns null when it isn't decodable. */
export function decodeCfEmailToken(hex: string): string | null {
  if (!/^[0-9a-f]+$/i.test(hex) || hex.length < 4 || hex.length % 2 !== 0) {
    return null
  }
  const key = parseInt(hex.slice(0, 2), 16)
  if (Number.isNaN(key)) return null

  let out = ''
  for (let i = 2; i < hex.length; i += 2) {
    const byte = parseInt(hex.slice(i, i + 2), 16)
    if (Number.isNaN(byte)) return null
    out += String.fromCharCode(byte ^ key)
  }
  // A decoded value that isn't address-shaped means we guessed wrong about the
  // markup; leaving the original alone is better than printing mojibake.
  return out.includes('@') ? out : null
}

/**
 * Replace every Cloudflare-obfuscated anchor with its real address.
 * A page with no markers is returned unchanged, so this is safe to run on
 * every preview.
 */
export function decodeCfEmailObfuscation(html: string): string {
  if (!html || !html.includes('__cf_email__')) return html

  return html.replace(
    /<a\b[^>]*\bdata-cfemail="([0-9a-fA-F]+)"[^>]*>.*?<\/a>/gi,
    (whole, hex: string) => decodeCfEmailToken(hex) ?? whole,
  )
}
